/**
 * TipTap inline node for merge tags.
 *
 * Renders as an atomic chip inside the editor (single deletable
 * unit, can't get split when the caret lands inside it) but
 * serialises to a span like:
 *
 *   <span data-mt="profile.first">{{ profile.first }}</span>
 *
 * The literal `{{ profile.first }}` lives INSIDE the span so the
 * Stampede send service's Mustache substitution still finds it on
 * the wire — the wrapper span is just a hint for the editor to
 * re-parse it as a chip on next load. Triple-brace tags (the
 * voucher QR) are stored verbatim too: `{{{ voucher_code.qr }}}`.
 */

import { Node, mergeAttributes } from "@tiptap/core"

export interface MergeTagNodeAttrs {
  /** Wire string with braces (`{{ profile.first }}`). */
  value: string
  /** Dotted key without braces (`profile.first`). */
  key: string
  /** Display label in the chip (e.g. "First Name"). */
  label: string
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    mergeTag: {
      /**
       * Insert a merge-tag chip at the current selection. Pass
       * the wire `value` (with braces), the bare `key`, and the
       * human `label`.
       */
      insertMergeTag: (attrs: MergeTagNodeAttrs) => ReturnType
    }
  }
}

export const MergeTagNode = Node.create({
  name: "mergeTag",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      value: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-value") ?? el.textContent ?? "",
        renderHTML: (attrs) => ({ "data-value": attrs.value }),
      },
      key: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-mt") ?? "",
        renderHTML: (attrs) => ({ "data-mt": attrs.key }),
      },
      label: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-label") ?? "",
        renderHTML: (attrs) =>
          attrs.label ? { "data-label": attrs.label } : {},
      },
    }
  },

  parseHTML() {
    return [
      {
        // Match the canonical save shape — a span with `data-mt`
        // is unambiguously one of our chips. Anything that looks
        // like `{{ x.y }}` outside such a span stays as plain text
        // (the operator can convert via the picker).
        tag: "span[data-mt]",
      },
    ]
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        class: "mt-chip",
      }),
      // Keep the literal placeholder INSIDE the span so the
      // service still sees it in the email HTML.
      String(node.attrs.value ?? ""),
    ]
  },

  addCommands() {
    return {
      insertMergeTag:
        (attrs) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs,
          })
        },
    }
  },
})
